import { Navigate, createBrowserRouter, type RouteObject } from "react-router-dom";
import { ProtectedRoute } from "@/auth";
import { AppShell } from "./AppShell";
import { LoginPage } from "./LoginPage";
import { ForgotPasswordPage } from "./ForgotPasswordPage";
import { ResetPasswordPage } from "./ResetPasswordPage";
import { DemandeAccesPage } from "./DemandeAccesPage";
import { ErrorBoundary } from "./ErrorBoundary";
import { FEATURES } from "./registry";

/**
 * The router — composed from the FEATURE REGISTRY. Each feature's RouteObjects are mounted under the
 * authenticated <AppShell> layout and individually wrapped in <ProtectedRoute> with the module's
 * requiredCapabilities, so a feature declares routes/caps and the router does the gating. Features
 * NEVER edit this file.
 */
function wrapFeatureRoutes(): RouteObject[] {
  return FEATURES.flatMap((mod) =>
    mod.routes.map((route) => ({
      ...route,
      element: (
        <ProtectedRoute requiredCapabilities={mod.requiredCapabilities}>
          <ErrorBoundary>{route.element}</ErrorBoundary>
        </ProtectedRoute>
      ),
    })),
  );
}

/** The first feature route (lowest order) is the post-login landing. */
function defaultPath(): string {
  const navItems = FEATURES.flatMap((m) => m.nav);
  navItems.sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
  return navItems[0]?.path ?? "/";
}

export const router = createBrowserRouter([
  // ── Public surfaces (outside the AppShell guard — see PUBLIC_PATHS) ────────────
  { path: "/login", element: <LoginPage /> },
  { path: "/mot-de-passe-oublie", element: <ForgotPasswordPage /> },
  { path: "/reinitialiser", element: <ResetPasswordPage /> },
  { path: "/demande-acces", element: <DemandeAccesPage /> },
  // ── Authenticated shell ───────────────────────────────────────────────────────
  {
    path: "/",
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to={defaultPath()} replace /> },
      ...wrapFeatureRoutes(),
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
